<template>
  <a-modal title="Filters" width="600px" :visible="visible" @cancel="cancel" :destroy-on-close="true" v-if="visible">
    <div class="grid" :style="{ overflow: 'auto', maxHeight: '650px' }">
      <div class="filter-section-title col-12">
        <span>Date range</span>
      </div>
      <b class="col-4">Start Date</b>
      <a-date-picker
        class="col-8"
        format="MM/DD/YYYY"
        v-model="filters.start_date"
        valueFormat="YYYY-MM-DD"
        placeholder="Start Date"
        @change="handleStartDateChange('start_date', 'end_date')"
      />

      <b class="col-4">End Date</b>
      <a-date-picker
        class="col-8"
        format="MM/DD/YYYY"
        v-model="filters.end_date"
        valueFormat="YYYY-MM-DD"
        placeholder="End Date"
        ref="endDatePicker"
        @change="handleEndDateChange('start_date', 'end_date')"
        :open="isEndDatePickerOpen"
        :default-picker-value="getEndDateDefaultPickerValue('start_date')"
        @openChange="handleEndDateOpenChange"
      />

      <b class="col-4">Associates</b>
      <search-associates
        class="col-8"
        :is-associate-id="true"
        :multiply="true"
        :value="filters.associates"
        placeholder="Search associates"
        data-testid="shifts-filter-associates-select"
        :filter="associateSearchFilter"
        @update:value="updateAssociates"
      />

      <template v-if="!hasAssociates">
        <b class="col-4">Department</b>
        <custom-select
          class="col-8"
          :multiple="true"
          :value="filters.departments"
          placeholder="Department"
          :options="departments"
          name="name"
          option-key="id"
          data-testid="shifts-filter-departments-select"
          @update:value="updateFilter('departments', $event)"
        />

        <b class="col-4">Position</b>
        <custom-select
          class="col-8"
          :multiple="true"
          :value="filters.roles"
          placeholder="Position"
          :options="roles"
          name="name"
          option-key="id"
          data-testid="shifts-filter-positions-select"
          @update:value="updateFilter('roles', $event)"
        />

        <template v-if="isShowPaymentType">
          <b class="col-4">Payment Type</b>
          <custom-select
            class="col-8"
            :multiple="true"
            :value="filters.payment_types"
            placeholder="Payment Type"
            :options="paymentTypes"
            name="value"
            option-key="key"
            data-testid="shifts-filter-payment-types-select"
            @update:value="updateFilter('payment_types', $event)"
          />
        </template>

        <template v-if="!filters.payment_types?.length || filters.payment_types.includes('contractor')">
          <b class="col-4">Legal Entity</b>
          <custom-select
            class="col-8"
            :multiple="true"
            :value="filters.legal_entities"
            placeholder="Legal Entity"
            :options="legalEntities"
            name="name"
            option-key="id"
            data-testid="shifts-filter-legal-entities-select"
            @update:value="updateFilter('legal_entities', $event)"
          />
        </template>

        <b class="col-4">Profile Status</b>
        <custom-select
          class="col-8"
          :multiple="true"
          :value="filters.associate_statuses"
          placeholder="Status"
          :options="associateStatuses"
          name="name"
          option-key="key"
          data-testid="shifts-filter-profile-statuses-select"
          @update:value="updateFilter('associate_statuses', $event)"
        />

        <b class="col-4">Pay Statuses</b>
        <custom-select
          class="col-8"
          :multiple="true"
          :value="filters.pay_statuses"
          placeholder="Pay Statuses"
          :options="payStatuses"
          name="value"
          option-key="key"
          data-testid="shifts-filter-pay-statuses-select"
          @update:value="updateFilter('pay_statuses', $event)"
        />

        <b class="col-4">Schedule Types</b>
        <custom-select
          class="col-8"
          :multiple="true"
          :value="filters.schedule_types"
          placeholder="Schedule Types"
          :options="scheduleTypes"
          name="value"
          option-key="key"
          data-testid="shifts-filter-schedule-types-select"
          @update:value="updateFilter('schedule_types', $event)"
        />
      </template>
    </div>
    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button :disabled="isEmptyFilters" @click="reset"> Reset </a-button>
        <a-button type="primary" @click="apply"> Apply </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Modal, Button, DatePicker } from 'ant-design-vue'
import { PAYMENT_TYPE, PAYMENT_WORK_STATUS } from '@/store/modules/payment/constants'
import { SCHEDULED_TYPE } from '@/store/modules/profile/constants'
import CustomSelect from '@/components/ui/custom-select.vue'
import { deepCopy } from '@/utils/deep-copy'
import SearchAssociates from '@/components/search-associates.vue'
import LinkedDateFilterMixin from '@/mixins/linkedDateFilterMixin'
import moment from 'moment-timezone'

const createFiltersBaseState = () => ({
  end_date: moment().format('YYYY-MM-DD'),
  start_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
  associates: undefined,
  departments: undefined,
  roles: undefined,
  payment_types: undefined,
  legal_entities: undefined,
  associate_statuses: undefined,
  pay_statuses: undefined,
  schedule_types: undefined
})

export default {
  mixins: [LinkedDateFilterMixin],
  components: {
    'a-modal': Modal,
    'a-button': Button,
    'a-date-picker': DatePicker,
    'search-associates': SearchAssociates,
    'custom-select': CustomSelect
  },
  data() {
    return {
      filters: createFiltersBaseState(),

      paymentTypes: Object.entries(PAYMENT_TYPE).map(([key, value]) => ({
        key,
        value
      })),

      payStatuses: Object.entries(PAYMENT_WORK_STATUS).map(([key, value]) => ({
        key,
        value
      })),

      scheduleTypes: Object.entries(SCHEDULED_TYPE).map(([key, value]) => ({
        key,
        value
      })),

      associateStatuses: [
        { key: 'active', name: 'Active' },
        { key: 'suspended', name: 'Blocked' },
        { key: 'terminated', name: 'Separated' },
        { key: 'disabled', name: 'Disabled' }
      ]
    }
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    currentFilters: {
      type: Object,
      required: true
    },
    clientId: {
      type: Number,
      default: undefined
    }
  },
  computed: {
    associateSearchFilter() {
      return {
        facility_project_ids: [this.clientId],
        staffing: true
      }
    },
    departments() {
      return this.$store.state.applicationState.projectDepartments
    },
    roles() {
      return this.$store.state.applicationState.projectRoles
    },
    legalEntities() {
      return this.$store.state.applicationState.projectLegalEntities
    },
    isEmptyFilters() {
      return JSON.stringify(createFiltersBaseState()) === JSON.stringify(this.filters)
    },
    hasAssociates() {
      return Boolean(this.filters.associates?.length)
    },
    isShowPaymentType() {
      return this.$store.state.applicationState.currentPaymentTypeName === 'All'
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          const currentFilters = deepCopy(this.currentFilters)

          this.filters = {
            ...createFiltersBaseState(),
            ...currentFilters
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    updateAssociates(value) {
      this.filters.associates = value || undefined
    },

    updateFilter(key, value) {
      this.filters[key] = value || undefined

      if (key === 'payment_types' && value?.length && !value.includes('contractor')) {
        this.filters.legal_entities = undefined
      }
    },

    reset() {
      if (this.isEmptyFilters) {
        this.cancel()
      } else {
        this.filters = createFiltersBaseState()
        this.$emit('update:currentFilters', this.filters)
      }
    },

    cancel() {
      this.$emit('update:visible', false)
    },

    apply() {
      this.cancel()

      this.$emit('update:currentFilters', deepCopy(this.filters))
    }
  }
}
</script>

<style scoped>
.ant-calendar-picker {
  margin: 0;
}

.filter-section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 6px 0 2px;
  color: var(--text-color-secondary);
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-transform: uppercase;
}

.filter-section-title::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-color-base);
}
</style>
